import React, { useMemo, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  Box,
  Icon,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

// Pre-computed gradients and constants for maximum performance
const GRADIENTS = {
  create: {
    topAccent: "linear-gradient(90deg, #10B981, #059669)",
    iconBg: "linear-gradient(135deg, #10B981, #059669)",
    buttonBg: "linear-gradient(135deg, #10B981, #059669)",
  },
  edit: {
    topAccent: "linear-gradient(90deg, #F59E0B, #D97706)",
    iconBg: "linear-gradient(135deg, #F59E0B, #D97706)",
    buttonBg: "linear-gradient(135deg, #F59E0B, #D97706)",
  }
};

// Pre-defined icon paths for performance
const ICON_PATHS = {
  create: "M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1L9 7V9C9 10.1 9.9 11 11 11V22H13V11C14.1 11 15 10.1 15 9Z",
  edit: "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
};

// Modal titles and subtitles as constants
const MODAL_CONFIG = {
  create: {
    title: "Додати категорію",
    subtitle: "Створення нової категорії"
  },
  edit: {
    title: "Редагувати категорію", 
    subtitle: "Оновлення існуючої категорії"
  }
};

// Optimized Modal Component moved outside to prevent recreation
const OptimizedModal = React.memo(({ 
  isOpen, 
  onClose, 
  onSubmit, 
  theme = 'create',
  colorModeValues,
  categoryOptions,
  formData,
  handleChange
}) => {
  const config = MODAL_CONFIG[theme];
  const gradients = GRADIENTS[theme];
  const iconPath = ICON_PATHS[theme];

  // Handle submit and close
  const handleSubmit = useCallback(async () => {
    try {
      await onSubmit();
      onClose(); // Close modal after successful submit
    } catch (error) {
      // If submit fails, don't close the modal
      console.error('Submit failed:', error);
    }
  }, [onSubmit, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(2px)" />
      <ModalContent
        bg={colorModeValues.cardBg}
        borderRadius="xl"
        border="1px solid"
        borderColor={colorModeValues.borderColor}
        overflow="hidden"
        boxShadow="xl"
        mx={4}
      >
        {/* Minimal gradient accent */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="4px"
          bg={gradients.topAccent}
        />
        
        {/* Streamlined Header */}
        <ModalHeader py={4} px={6}>
          <Box display="flex" alignItems="center">
            <Box
              p={2}
              bg={gradients.iconBg}
              borderRadius="lg"
              mr={3}
            >
              <Icon viewBox="0 0 24 24" boxSize={5} color="white">
                <path fill="currentColor" d={iconPath} />
              </Icon>
            </Box>
            <Box>
              <Text 
                fontSize="xl"
                fontWeight="bold"
                color={colorModeValues.labelColor}
                mb={1}
              >
                {config.title}
              </Text>
              <Text 
                fontSize="sm" 
                color={colorModeValues.subtextColor}
                fontWeight="medium"
              >
                {config.subtitle}
              </Text>
            </Box>
          </Box>
        </ModalHeader>
        
        <ModalCloseButton 
          top={3} 
          right={3}
          borderRadius="md"
          _hover={{ bg: colorModeValues.hoverBg }}
        />
        
        <ModalBody px={6} py={2}>
          <Box
            bg={colorModeValues.inputBg}
            p={5}
            borderRadius="lg"
            border="1px solid"
            borderColor={colorModeValues.borderColor}
          >
            <FormControl mb={4}>
              <FormLabel 
                fontWeight="semibold" 
                color={colorModeValues.labelColor}
                mb={2}
              >
                Назва категорії
              </FormLabel>
              <Input 
                name="name" 
                value={formData.name} 
                onChange={handleChange}
                bg={colorModeValues.cardBg}
                border="1px solid"
                borderColor={colorModeValues.borderColor}
                borderRadius="md"
                _hover={{
                  borderColor: theme === 'create' ? colorModeValues.greenBorder : colorModeValues.orangeBorder,
                }}
                _focus={{
                  borderColor: theme === 'create' ? colorModeValues.greenFocus : colorModeValues.orangeFocus,
                  boxShadow: `0 0 0 1px ${theme === 'create' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                }}
              />
            </FormControl>
            
            <FormControl>
              <FormLabel 
                fontWeight="semibold" 
                color={colorModeValues.labelColor}
                mb={2}
              >
                Батьківська категорія
              </FormLabel>
              <Select
                name="parent"
                placeholder="Без батьківської категорії"
                value={formData.parent}
                onChange={handleChange}
                bg={colorModeValues.cardBg}
                border="1px solid"
                borderColor={colorModeValues.borderColor}
                borderRadius="md"
                _hover={{
                  borderColor: theme === 'create' ? colorModeValues.greenBorder : colorModeValues.orangeBorder,
                }}
                _focus={{
                  borderColor: theme === 'create' ? colorModeValues.greenFocus : colorModeValues.orangeFocus,
                  boxShadow: `0 0 0 1px ${theme === 'create' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                }}
              >
                {categoryOptions}
              </Select>
            </FormControl>
          </Box>
        </ModalBody>
        
        <ModalFooter px={6} py={4}>
          <Button 
            bg={gradients.buttonBg}
            color="white"
            mr={3} 
            onClick={handleSubmit}
            borderRadius="md"
            px={6}
            _hover={{
              transform: "translateY(-1px)",
              transition: "transform 0.15s ease"
            }}
            _active={{
              transform: "translateY(0)",
            }}
          >
            Зберегти
          </Button>
          <Button 
            variant="ghost" 
            onClick={onClose}
            borderRadius="md"
            px={4}
            _hover={{ bg: colorModeValues.hoverBg }}
          >
            Скасувати
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

const CategoryModals = ({
  isCreateModalOpen,
  isEditModalOpen,
  onCloseCreate,
  onCloseEdit,
  onSubmitCreate,
  onSubmitEdit,
  categories,
  formData,
  setFormData,
}) => {
  // Define color mode values outside of callbacks to comply with Rules of Hooks
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');
  const labelColor = useColorModeValue('gray.700', 'gray.200');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const greenBorder = useColorModeValue('green.300', 'green.500');
  const greenFocus = useColorModeValue('green.400', 'green.400');
  const orangeBorder = useColorModeValue('orange.300', 'orange.500');
  const orangeFocus = useColorModeValue('orange.400', 'orange.400');

  // Memoize color values object to prevent recreation
  const colorModeValues = useMemo(() => ({
    cardBg,
    borderColor,
    inputBg,
    subtextColor,
    labelColor,
    hoverBg,
    greenBorder,
    greenFocus,
    orangeBorder,
    orangeFocus,
  }), [cardBg, borderColor, inputBg, subtextColor, labelColor, hoverBg, greenBorder, greenFocus, orangeBorder, orangeFocus]);

  // Memoized category options to prevent re-rendering
  const categoryOptions = useMemo(() => {
    const renderOptions = (categories, depth = 0) => {
      return categories.map((category) => (
        <React.Fragment key={category.id}>
          <option value={category.id}>
            {'—'.repeat(depth)} {category.name}
          </option>
          {category.children?.length > 0 && renderOptions(category.children, depth + 1)}
        </React.Fragment>
      ));
    };
    return renderOptions(categories);
  }, [categories]);

  // Memoized change handler
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, [setFormData]);

  return (
    <>
      {/* Create Category Modal - No Re-render on Typing */}
      <OptimizedModal
        isOpen={isCreateModalOpen}
        onClose={onCloseCreate}
        onSubmit={onSubmitCreate}
        theme="create"
        colorModeValues={colorModeValues}
        categoryOptions={categoryOptions}
        formData={formData}
        handleChange={handleChange}
      />

      {/* Edit Category Modal - No Re-render on Typing */}
      <OptimizedModal
        isOpen={isEditModalOpen}
        onClose={onCloseEdit}
        onSubmit={onSubmitEdit}
        theme="edit"
        colorModeValues={colorModeValues}
        categoryOptions={categoryOptions}
        formData={formData}
        handleChange={handleChange}
      />
    </>
  );
};

export default CategoryModals;
